"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var React = require("react");
var react_tracking_1 = require("react-tracking");
var lodash_1 = require("lodash");
var bind_1 = require("../bind");
var tracker_1 = require("../tracker");
var router_history_1 = require("../cms/lib/router_history");
var PropTypes = require("prop-types");
var qs = require("qs");
var debug_1 = require("../debug");
var window_wrapper_1 = require("../window_wrapper");
var user_context_helpers_1 = require("../user_context_helpers");
var user_context_provider_1 = require("./user_context_provider");
var classNames = require("classnames");
var route_settings_1 = require("../route_settings");
var link_parser_1 = require("../link_parser");
// For backwards compatibility?
var COMPONENT_NAME = 'TrackedLink';
var alwaysClientSide = false;
/**
 * For full SPA applications, we don't need to check if
 * a given route is client side enabled, they all are.
 */
function alwaysClientSideTransition(should) {
    alwaysClientSide = should;
}
exports.alwaysClientSideTransition = alwaysClientSideTransition;
/**
 * These functions will take the given route and the current routes set on the window
 * at `Reverb.Routes`, and pass them through React Router's match function. This will
 * resolve the route given the path. We can take the current window's location, check
 * to see that its route is clientSide and then compare the destination with the same
 * check.
 *
 * <Route
 *   key="feed"
 *   path="my/feed"
 *   props={{ clientSide: true }}
 *   ...
 *
 * This function returns a promise that will resolve to an array of booleans,
 * if both are true, you can issue a history.push() because your going from and
 * to a client side route. If not, just do a location.assign().
 */
function shouldClientSideResolve(nextLocation) {
    var toClientSide = isClientSide(nextLocation);
    var fromClientSide = isClientSide(window_wrapper_1.default.location);
    return Promise.all([fromClientSide, toClientSide]).then(function (_a) {
        var fromC = _a[0], toC = _a[1];
        return fromC && toC;
    });
}
exports.shouldClientSideResolve = shouldClientSideResolve;
function isClientSide(location) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        var settings;
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (alwaysClientSide) {
                        return [2 /*return*/, true];
                    }
                    return [4 /*yield*/, route_settings_1.getRouteSettings(location)];
                case 1:
                    settings = _a.sent();
                    if (settings.length === 0) {
                        return [2 /*return*/, false];
                    }
                    // Nested routes are returned from general to specific, check the most specific one
                    return [2 /*return*/, settings.pop().clientSide];
            }
        });
    });
}
/**
 * <CoreLink />
 *
 * Component for ALL client and server side links, this will provide tracking and
 * smart navigation from and to server or client routes. The routes must be configured
 * correctly to have client side behavior. If not they will fallback.
 *
 * ## Feature Set
 * * Is route aware, so if you tag a route with `props={{ clientSide: true }}`,
 *   CoreLink will perform a client side transition if you're on and going to a `clientSide` route
 *
 * * All clicks will be tracked using react-tracking. You can pass in overrides for the tracking if needed.
 *
 * * CoreLink will fallback to a normal link if you're in a WebView.
 *   This allows native apps to intercept native click events, and re-route to deep links if needed.
 *
 * * CoreLink accepts a `to` param, that can either be a full url, a relative url, or
 *   any more complex options that the `<Link>` component would normally
 *   accept (i.e. `{ pathname: '/foo', query: { foo: 'bar' } }`)
 *
 * * CoreLink knows when it is "active" even if its rendered outside the current router
 *   context, no need to track active state.
 *
 * * With CoreLink you should _almost_ never have to use the history object directly, just build the appropriate link.
 *
 * * There is no need to use CoreLink in LP or Sites, just use Link.  CoreLink will correctly work in full
 *   SPA applications, but it is not needed.
 */
var CoreLink = /** @class */ (function (_super) {
    tslib_1.__extends(CoreLink, _super);
    function CoreLink() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    CoreLink.prototype.handleClick = function (evt, url, useNativeEvent) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var additionalTrackingData, clientSide, hash, pathname, query, search, q, path, e_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        additionalTrackingData = this.props.tracking.getTrackingData();
                        this.props.tracking.trackEvent(tslib_1.__assign({ componentName: this.props.trackName || COMPONENT_NAME, interactionType: this.props.interactionType || tracker_1.CLICK, entityId: this.props.entityId, position: this.props.position, url: this.props.to }, additionalTrackingData));
                        if (this.props.onClick) {
                            this.props.onClick(evt);
                        }
                        // If a modifier key is hit, just let the browser do its thing
                        // this will allow for "normal" behavior.
                        if (!useNativeEvent && !(evt.metaKey || evt.altKey || evt.ctrlKey)) {
                            evt.preventDefault();
                        }
                        else {
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, shouldClientSideResolve(url)];
                    case 2:
                        clientSide = _a.sent();
                        hash = url.hash, pathname = url.pathname, query = url.query;
                        search = void 0;
                        q = qs.stringify(query);
                        if (q) {
                            search = "?" + q;
                        }
                        if (clientSide) {
                            debug_1.debug("client side transition - " + pathname + (search || ''));
                            path = lodash_1.pickBy({ pathname: pathname, search: search, hash: hash }, lodash_1.identity);
                            if (this.props.replace) {
                                router_history_1.default.replace(path);
                            }
                            else {
                                router_history_1.default.push(path);
                            }
                        }
                        else {
                            debug_1.debug("server side transition - " + pathname + (search || ''));
                            window_wrapper_1.default.location.assign(link_parser_1.serializeURL(url));
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _a.sent();
                        debug_1.debug("could not transition - " + e_1);
                        window.location.assign(link_parser_1.serializeURL(url));
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    CoreLink.prototype.componentDidMount = function () {
        var _this = this;
        // If there is no router context, listen for route changes and schedule an update
        // This is mostly for category nav links, which are rendered outside of the router.
        if (!this.context.router) {
            this.stop = router_history_1.default.listen(function () {
                setTimeout(function () { return _this.forceUpdate(); }, 0);
            });
        }
    };
    CoreLink.prototype.componentWillUnmount = function () {
        if (this.stop) {
            this.stop();
        }
    };
    CoreLink.prototype.render = function () {
        var _a;
        var _this = this;
        var url = link_parser_1.parseURL(this.props.to);
        var useNativeEvent = (!url ||
            user_context_helpers_1.inMobileApp(this.props.user) || // Do not render client side links in Webviews, they don't trigger transitions
            window_wrapper_1.default.location.origin !== url.origin ||
            window_wrapper_1.default.location.href === 'about:blank' ||
            !url.pathname ||
            this.props.target);
        var active = link_parser_1.isActive(url, window_wrapper_1.default.location);
        var classes = classNames(this.props.className, (_a = {}, _a[this.props.activeClassName] = this.props.activeClassName && active, _a));
        var href = link_parser_1.serializeURL(url);
        return (React.createElement("a", { id: this.props.id, className: classes, href: href, rel: this.props.rel, onClick: function (evt) { _this.handleClick(evt, url, useNativeEvent); }, style: this.props.style, target: this.props.target }, this.props.children));
    };
    // Inject the router, and see if we can use it.
    CoreLink.contextTypes = {
        router: PropTypes.object,
    };
    tslib_1.__decorate([
        bind_1.default
    ], CoreLink.prototype, "handleClick", null);
    return CoreLink;
}(React.Component));
exports.CoreLink = CoreLink;
exports.default = react_tracking_1.default({ eventType: tracker_1.INTERACTION })(user_context_provider_1.withUserContext(CoreLink));
//# sourceMappingURL=core_link.js.map


//////////////////
// WEBPACK FOOTER
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/commons/dist/components/core_link.js
// module id = dkm0
// module chunks = 20 21